import React from "react";
import clsx from "clsx";
import {
  DialogTrigger,
  Modal,
  Dialog,
  ModalOverlay,
  DialogRenderProps,
} from "react-aria-components";

import Button, { ButtonProps } from "../forms/button";
import { PalletLangContext } from "../../lang-provider";

import styles from "./sidebar-overlay.module.css";

/**
 * How wide the panel is.
 *
 * `"standard"` (the default) is the fixed 640px every drawer has had. `"half"`
 * is for a panel that holds tables: half the window, but never narrower than
 * 480px, so a very small window does not get a sliver.
 */
export type SidebarOverlayWidth = "standard" | "half";

/** The panel's contents: both slots take a node or a render function. */
type SidebarOverlayContent = {
  children:
    | ((dialogRenderProps: DialogRenderProps) => React.ReactNode)
    | React.ReactNode;
  header:
    | ((dialogRenderProps: DialogRenderProps) => React.ReactNode)
    | React.ReactNode;
};

/**
 * How the panel's open/closed state is driven, as a tagged union over
 * `control` -- the same per-arm idiom `Button` uses for `kind`.
 *
 * `"trigger"` is the button-owned case: `SidebarOverlay` renders the button
 * itself as `DialogTrigger`'s first child and react-aria owns open/closed.
 * `"external"` is the case a table row opens: no trigger is rendered at all
 * and the caller's own state is the only answer to "is it open", which is why
 * this arm renders no `DialogTrigger` -- a `DialogTrigger`'s internal state
 * would be a second, competing one.
 *
 * Each arm carries only the props that arm needs, so a controlled overlay
 * cannot also be handed a trigger, and an `"external"` overlay with no way to
 * close itself does not compile.
 */
type SidebarOverlayControl =
  | { control: "trigger"; trigger: ButtonProps }
  | {
      control: "external";
      isOpen: boolean;
      onOpenChange: (isOpen: boolean) => void;
    };

export type SidebarOverlayProps = SidebarOverlayContent &
  SidebarOverlayControl & { width?: SidebarOverlayWidth };

function SidebarOverlay(props: SidebarOverlayProps) {
  const { header, children, width = "standard" } = props;
  const langContext = React.useContext(PalletLangContext);
  const closeLabel = langContext?.simpleTranslate("actions.close") ?? "Close";

  // The dialog takes its accessible name from whatever the caller rendered
  // into `header`. Under `control: "external"` there is no `DialogTrigger` to
  // fall back on for an `aria-labelledby`, so without this react-aria would
  // warn and the panel would go unnamed.
  const headerId = React.useId();

  const panel = (
    <ModalOverlay
      isDismissable={true}
      className={styles.modalOverlay}
      shouldCloseOnInteractOutside={() => {
        return true;
      }}
      {...(props.control === "external"
        ? { isOpen: props.isOpen, onOpenChange: props.onOpenChange }
        : {})}
    >
      <Modal
        className={({ isEntering }) =>
          clsx(styles.sidebar, {
            [styles.half]: width === "half",
            [styles.loading]: isEntering,
          })
        }
      >
        <Dialog className={styles.dialog} aria-labelledby={headerId}>
          {(actions) => (
            <div className={styles.inner}>
              <div className={styles.header}>
                <div id={headerId} className={styles.headerContent}>
                  {typeof header === "function" ? header(actions) : header}
                </div>
                <Button
                  kind="icon-only"
                  label={closeLabel}
                  slot="close"
                  variant="tertiary"
                  icon="X"
                />
              </div>
              {typeof children === "function" ? children(actions) : children}
            </div>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );

  if (props.control === "external") {
    return panel;
  }

  return (
    <DialogTrigger>
      <Button {...props.trigger} />
      {panel}
    </DialogTrigger>
  );
}

export default SidebarOverlay;
